/**
 * One Payment Terms vocabulary, shared by recaps, offers and invoices.
 *
 * The recap used to store a contract-clause NAME ("Letter of Credit") from a
 * four-row coal preset table, while invoices stored a foreign key into a
 * separate catalogue that computes due dates. Nothing joined the two, so a
 * term picked on a recap never reached the invoice. Both now read
 * /api/offer/payment_presets/, which serves the merged catalogue.
 *
 * Stored form is the TERM CODE ("NET45"). Codes are stable across dev and
 * prod; primary keys are not. Rows written before the merge hold the old
 * display name, so every lookup here falls back to matching on name.
 */

/** Find the catalogue row for a stored value: term code first, then legacy name. */
export const findPaymentTerm = (terms, value) => {
  if (!value || !Array.isArray(terms)) return null;
  const wanted = String(value).trim().toLowerCase();
  return (
    terms.find((t) => String(t?.term_code ?? '').toLowerCase() === wanted) ||
    terms.find((t) => String(t?.name ?? t?.description ?? '').toLowerCase() === wanted) ||
    null
  );
};

/** The contract wording a term inserts into an offer, '' for day-count terms. */
export const paymentTermWording = (terms, value) =>
  findPaymentTerm(terms, value)?.wording || '';

/** Catalogue rows as {value, label} for a <select>. */
export const paymentTermOptions = (terms) =>
  (Array.isArray(terms) ? terms : []).map((t) => ({
    value: t.term_code ?? t.name,
    label: t.description ?? t.name,
    days_until_due: t.days_until_due ?? null,
  }));

/**
 * Keep a saved value visible even when it is not in the catalogue.
 *
 * A recap saved before the merge holds a display name, and a <select> whose
 * value matches no option renders BLANK -- the trader would open the deal and
 * see their agreed payment term silently gone. Appending the stored value as
 * its own option shows the truth instead.
 */
export const withStoredOption = (options, value) => {
  if (!value) return options;
  const has = options.some((o) => String(o.value).toLowerCase() === String(value).toLowerCase());
  return has ? options : [...options, { value, label: value, days_until_due: null }];
};
